import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Box, CircularProgress } from '@mui/material';

// api
import BuildingApi from '../../api/building.api';
import TermApi from '../../api/term.api';
import { useAppSelector } from '../../hooks/hooks';

// comps
import FilterContainer from '../../components/layout/filterContainer';
import ContentContainer from '../../components/layout/contentContainer';
import GlobalDropdown from '../../components/global/dropdown';
import GlobalButton from '../../components/global/button';
import GlobalInput from '../../components/global/input';
import RoomConflictsGrid from '../../components/grid/roomConflictsGrid';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter-icon.svg';

// constants
import { colors } from '../../theme';
import { MOMENT_TIME_FORMAT } from '../../constants/date_format';

const RoomConflicts = (props: any) => {
    const _buildingApi = new BuildingApi();
    const _termApi = new TermApi();

    const appState = useAppSelector((state) => state.appState);
    const [resetPage, setResetPage] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(true);

    const defaultFilterObject = {
        date: null,
        subject: null,
        room: null,
        instructor: null,
        timeIn: '00:00',
        timeOut: '23:59',
    };

    const [state, setState] = useState({
        terms: [],
        conflictsData: [],
        selectedTerm: null,
        isLoading: false,
        isFilterOpen: false,
        filterObject: defaultFilterObject,
        isClearAll: false,
        isFilteredGrid: false,
    });

    useEffect(() => {
        if (!Object.values(state.filterObject).every((o) => o === '' || !o)) {
            setResetPage(true);
        } else {
            setResetPage(false);
            setButtonDisabled(true);
        }
    }, [state.filterObject]);

    useEffect(() => {
        if (state.selectedTerm) {
            const startDate = moment(state.selectedTerm.startDate).toDate();
            const endDate = moment(state.selectedTerm.endDate).toDate();
            // TODO: remove hard-coded buildingId (PS)
            loadData(47, startDate, endDate).catch((error) => toast.error(error));
        }
    }, [state.selectedTerm]);

    useEffect(() => {
        loadTermCampuses(appState.campus?.id).catch((error) => toast.error(error));
    }, [appState]);

    const loadData = async (buildingId: number, startDate: Date, endDate: Date) => {
        setState({ ...state, isLoading: true });
        const conflictsData = await _buildingApi.GetConflicts(buildingId, startDate, endDate);

        setState({ ...state, conflictsData: conflictsData?.rooms, isLoading: false });
    };

    const loadTermCampuses = async (campusId) => {
        const termCampuses = await _termApi.GetTermsByCampusId(campusId);

        setState({
            ...state,
            terms: termCampuses,
            selectedTerm: appState?.term,
        });
    };

    const handleDropdownChange = (name: any, value: any) => {
        setState({
            ...state,
            [name]: value,
        });
    };

    const handleFitlerObj = (name: any, value: any) => {
        if (value === '') value = null;

        setState({
            ...state,
            filterObject: {
                ...state.filterObject,
                [name]: name === 'subject' ? value.toUpperCase() : value,
            },
        });
        setButtonDisabled(false);
    };

    const handleFilterComp = () => {
        setState({
            ...state,
            isFilterOpen: !state.isFilterOpen,
        });
    };

    const handleClearAll = () => {
        setState({
            ...state,
            filterObject: defaultFilterObject,
            selectedTerm: appState?.term,
            isClearAll: true,
            isFilteredGrid: false,
        });
        setButtonDisabled(true);
    };

    const getGridData = () => {
        let roomConflicts = [...state.conflictsData];
        const { room } = state.filterObject;

        if (state.isFilteredGrid) {
            if (room) {
                roomConflicts = roomConflicts.filter((r) => r.roomNumber.startsWith(room));
            }
        }
        return roomConflicts;
    };

    const handleFilter = () => {
        const { timeIn, timeOut } = state.filterObject;
        if (timeIn?.length >= 4 && moment(timeIn, MOMENT_TIME_FORMAT).isAfter(moment(timeOut, MOMENT_TIME_FORMAT))) {
            toast.error('Start Time must be BEFORE End Time.');
            return;
        }
        if (timeOut?.length >= 4 && moment(timeOut, MOMENT_TIME_FORMAT).isBefore(moment(timeIn, MOMENT_TIME_FORMAT))) {
            toast.error('End Time must be AFTER Start Time.');
            return;
        }
        setState({ ...state, isFilteredGrid: true });
    };

    return (
        <>
            <FilterContainer sx={{ flexDirection: 'column' }}>
                <FlexBox sx={{ justifyContent: 'space-between' }}>
                    <GlobalDropdown
                        label="term"
                        placeholder="Select a term"
                        data={state.terms}
                        value={state.selectedTerm}
                        field={'termId'}
                        name={'termCode'}
                        dataItemKey={'selectedTerm'}
                        onChange={handleDropdownChange}
                        width="120px"
                    />
                    <GlobalButton variant="outlined" text="filters" startIcon={<FilterIcon />} onClick={handleFilterComp} sx={{ width: '10%' }} />
                </FlexBox>
                {state.isFilterOpen && (
                    <FlexBox sx={{ justifyContent: 'space-between' }}>
                        <FlexBox sx={{ mt: '10px', flexDirection: 'column' }}>
                            <FlexBox>
                                <GlobalInput label="filter" icon={<FilterIcon />} placeholder="Room" name={'room'} value={state.filterObject.room} onChange={handleFitlerObj} sx={{ width: '120px' }} />
                                <GlobalInput placeholder="Subject" name={'subject'} value={state.filterObject.subject} onChange={handleFitlerObj} sx={{ width: '120px' }} />
                            </FlexBox>
                            <FlexBox sx={{ mt: '10px' }}>
                                <GlobalInput
                                    label="Conflict Date"
                                    placeholder="Conflict Date"
                                    type="date"
                                    name={'date'}
                                    value={state.filterObject.date}
                                    onChange={handleFitlerObj}
                                    sx={{ width: '160px' }}
                                />
                                <GlobalInput label="Time In" placeholder="Time In" isTime={true} name={'timeIn'} value={state.filterObject.timeIn} onChange={handleFitlerObj} sx={{ width: '125px' }} />
                                <GlobalInput
                                    label="Time Out"
                                    placeholder="Time Out"
                                    isTime={true}
                                    name={'timeOut'}
                                    value={state.filterObject.timeOut}
                                    onChange={handleFitlerObj}
                                    sx={{ width: '125px' }}
                                />
                            </FlexBox>
                        </FlexBox>
                        <FlexBox sx={{ mt: '10px', width: '10%', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                            <GlobalButton variant="round" text="clear all" fullWidth secondary={'true'} endIcon={<RefreshIcon />} onClick={handleClearAll} />
                            <GlobalButton variant="round" text="search" disabled={buttonDisabled} fullWidth sx={{ marginTop: '5px' }} onClick={handleFilter} />
                        </FlexBox>
                    </FlexBox>
                )}
            </FilterContainer>
            {state.conflictsData.length > 0 && (
                <Box>
                    <RoomConflictsGrid data={getGridData()} filterObject={state.filterObject} isFilteredGrid={state.isFilteredGrid} />
                </Box>
            )}
            {state.isLoading && (
                <ContentContainer>
                    <FlexBox sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={100} />
                    </FlexBox>
                </ContentContainer>
            )}
        </>
    );
};

export default RoomConflicts;

const FlexBox = styled(Box)`
    && {
        display: flex;

        & > * {
            margin-right: 10px;
        }

        & > :last-child {
            margin-right: 0px;
        }
    }
`;
